import React from 'react';
import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import {
    Box,
    Button,
    ButtonGroup,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormLabel,
    IconButton,
    Radio,
    InputAdornment,
    RadioGroup,
    TextField,
    Tooltip
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import { styled } from "@mui/material/styles";
import { blueGrey } from "@mui/material/colors";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { RootState, useAppDispatch, useAppSelector } from "../../../state/state.tsx";
import { closeModal, toggleLoading } from "../../../state/ModalSlice/ModalSlice.ts";
import { useBackend } from "../../../hooks/backend/useBackend.ts";
import { LiveSubscriptionType } from "../../Subscriptions/types/LiveSubscriptionTypes.ts";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../Config/firebase.ts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { ActionType, SubscriptionType } from '../../../enums.ts';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2.ts';
import { logAction } from '../utilityFunctions.ts';
import { CustomerKYCFieldType } from './CustomerKYCModal';


// -------------------- Counter Config
const StyledButton = styled(Button)(({ theme }) => ({
    color: theme.palette.getContrastText(blueGrey[50]),
    backgroundColor: blueGrey[50],
    borderColor: blueGrey[200],
    height: '35px', // Set height to 30px
    "&:hover": {
        backgroundColor: blueGrey[100], borderColor: blueGrey[300],
    },
}));




const StyledInput = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        "& fieldset": {
            borderRadius: 0, borderColor: blueGrey[200], height: '40px', // Set height to 30px
        }, "&:hover fieldset": {
            borderColor: blueGrey[300],
        }, "&.Mui-focused fieldset": {
            borderColor: blueGrey[500],
        }, "& input": {
            textAlign: "center", width: 60, color: blueGrey[700], height: '100%', // Ensure input takes full height of the container
            padding: '10 0 0 0', // Remove padding to maintain the 30px height
        },
    },
});

function normalizeSubscriptionCycleEntry(raw: any, subType: string): any {
    if (subType === SubscriptionType.Count) {
        const count = raw.count ?? raw.Count;
        const price = raw.price ?? raw.Price;
        const validity = raw.validity ?? raw.Validity;
        return {
            billingCycle: raw.billingCycle || "Count",
            count: Number(count),
            price: Number(price),
            validity: Number(validity),
        };
    }
    return {
        billingCycle: raw.billingCycle,
        price: Number(raw.price ?? raw.Price ?? 0),
        validity: Number(raw.validity ?? raw.Validity ?? 0),
    };
}

function assignedPlanIndexInArray(plans: any[], docPlan: any, subType: string): number {
    if (!plans?.length || !docPlan) return -1;
    if (subType === SubscriptionType.Count) {
        return plans.findIndex(
            (p) =>
                Number(p.count) === Number(docPlan.count) &&
                Number(p.validity) === Number(docPlan.validity)
        );
    }
    return plans.findIndex((p) => (p.billingCycle || "") === (docPlan.billingCycle || ""));
}

/** Shape to store on LiveSubscriptions.assignedPlans when adding from subscription catalog. */
function livePlanEntryFromDocPlan(docPlan: any, subType: string): any {
    if (subType === SubscriptionType.Count) {
        return {
            billingCycle: docPlan.billingCycle || "Count",
            count: Number(docPlan.count),
            price: Number(docPlan.price),
            validity: Number(docPlan.validity),
        };
    }
    return {
        billingCycle: docPlan.billingCycle,
        price: Number(docPlan.price),
        validity: Number(docPlan.validity),
    };
}


export default function EditLiveSubscription() {

    // ------------------------DialogHandlers
    const Dispatch = useAppDispatch();
    const open = useAppSelector((state: RootState) => state.ModalSlice.EditLiveSubscriptionModalOpen);
    const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading)
    const handleClose = () => {
        resetState();
        Dispatch(closeModal("EditLiveSubscriptionModalOpen"))

    };
    const ServiceProviderId = useAppSelector(selectEffectiveUID)

    // ------------------------Form States

    const [profilePhoto, setProfilePhoto] = useState<string>('https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FProfilePhoto2Update.png?alt=media&token=53ea668c-04f3-493a-911d-15eba1ce897c');
    const [subscriberName, setSubscriberName] = useState<string>("")
    const [discount, setDiscount] = React.useState<string>("0")
    const [multiplier, setMultiplier] = useState<number>(1);
    const [, setIsError] = React.useState<boolean>(false)
    const [showMultiplier, setShowMultiplier] = React.useState<boolean>(false)
    const [monthlyPrice, setMonthlyPrice] = useState<string>("");
    const [products, setProducts] = useState([{ name: '', price: '0' }]);
    const [productErrors, setProductErrors] = useState<{ name: boolean; price: boolean }[]>([{ name: false, price: false }]);
    const [email, setEmail] = useState<string>("");
    const [secondaryMobile, setSecondaryMobile] = useState<string>("");
    const [cycle, setCycle] = useState<string>("");
    const [secondaryPayeeName, setSecondaryPayeeName] = useState<string>("");
    const [secondaryEmail, setSecondaryEmail] = useState<string>("");
    const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
    const [billingStart, setBillingStart] = useState<Date>(new Date());
    const [billingEnd, setBillingEnd] = useState<Date>(new Date());
    const [remainingCount, setRemainingCount] = useState<string>("0");
    const [customPrice, setCustomPrice] = useState<string>("");
    const [selectedCyclePlan, setSelectedCyclePlan] = useState<any>("");
    const [cyclePriceArray, setCyclePriceArray] = useState<any[]>([]);
    const [selectedCyclePlanIndex, setSelectedCyclePlanIndex] = useState<number>(0);
    /** Normalized cyclePriceArray from the subscription Firestore doc (display only). */
    const [subscriptionDocPlans, setSubscriptionDocPlans] = useState<any[]>([]);
    const [showChangePlan, setShowChangePlan] = useState<boolean>(false);
    const [outsideState, setOutsideState] = useState<boolean>(false);
    const [prevPrice, setPrevPrice] = useState<number>(0);
    const [payeeName, setPayeeName] = useState<string>("");
    const [prevCyclePlanIndex, setPrevCyclePlanIndex] = useState<number>(0);
    const [consumerGst, setConsumerGst] = useState<string>("");
    const [consumerGstError, setConsumerGstError] = useState<boolean>(false);
    const [changeToActive, setChangeToActive] = useState(false);
    const [prevBillingStart, setPrevBillingStart] = useState<Date>(new Date());
    const [prevBillingEnd, setPrevBillingEnd] = useState<Date>(new Date());
    
    // CustomerKYC related states
    const [customerKYCFields, setCustomerKYCFields] = useState<CustomerKYCFieldType[]>([]);
    const [kycResponses, setKycResponses] = useState<{[key: number]: any}>({});



    const [excludeLiveSubFromGST, setExcludeLiveSubFromGST] = useState<boolean>(false)

    // -----------------------Fetch states from Store

    // Reset

    const resetState = () => {

        setMultiplier(1);
        setIsError(false);
        // setProducts([{ name: '', price: '' }]);
        setProductErrors([{ name: false, price: false }]);
        setModalError({
            subscriberName: false,
            payeeNumber: false,
            CollectivePackPrice: false,
            monthlyPrice: false,
            email: false,
            secondaryMobile: false,
            secondaryEmail: false,
            secondaryPayeeName: false,
            customPrice: false,
            payeeName: false,
            consumerGst: false
        });
        setSecondaryPayeeName("");
        setSecondaryEmail("");
        setShowAdvanced(false);
        setSelectedCyclePlanIndex(0);
        setSelectedCyclePlan("");
        setCyclePriceArray([]);
        setSubscriptionDocPlans([]);
        setShowChangePlan(false);
        setPayeeName("");
        setConsumerGst("");
        setConsumerGstError(false);
        setChangeToActive(false);
        // Reset KYC responses
        setKycResponses({});
        setCustomerKYCFields([]);
        setExcludeLiveSubFromGST(false);
        setPrevBillingStart(new Date());
        setPrevBillingEnd(new Date());

    };

    const { uid: spId, display_name: spName } = useAppSelector((state: RootState) => state.userSliceV2?.data) || {}
    const LiveSubscriptionId = useAppSelector((state: RootState) => state.ArbitrarySlice.ForEditLiveSubscriptionModal.LiveSubscriptionId)
    const SubscriptionPrice = useAppSelector((state: RootState) => state.ArbitrarySlice.ForEditLiveSubscriptionModal.SubscriptionPrice)
    const NoGSTBatch = useAppSelector((state: RootState) => state.ArbitrarySlice.ForEditLiveSubscriptionModal.NoGSTBatch) || false

    // All Live Subs

    const AllLiveSubs = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data)
    const SelectedLiveSubscription = AllLiveSubs.filter((data: LiveSubscriptionType) => data.id === LiveSubscriptionId)[0];

    const handleChangeExcludeLiveSubFromGST = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExcludeLiveSubFromGST(event.target.checked);
    };

    useEffect(() => {
        if (SelectedLiveSubscription) {
            setShowChangePlan(false);
            setSubscriptionDocPlans([]);
            setOutsideState(SelectedLiveSubscription.outsideState)
            setShowMultiplier(SelectedLiveSubscription.serviceType === "Preschool" || SelectedLiveSubscription.serviceType === "Sports" || SelectedLiveSubscription.serviceType === "Creche" || SelectedLiveSubscription.serviceType === "Tution" || SelectedLiveSubscription.serviceType === "Tuition" || SelectedLiveSubscription.serviceType === "Fitness" || SelectedLiveSubscription.serviceType === "Other")
            setDiscount(SelectedLiveSubscription?.discountNegotiated?.toString() || "0")
            setMultiplier(SelectedLiveSubscription.priceMultiplier)
            setProfilePhoto(SelectedLiveSubscription.photoUrl)
            setSubscriberName(SelectedLiveSubscription.studentName)
            setMonthlyPrice(SelectedLiveSubscription.Price ? SelectedLiveSubscription.Price.toString() : SubscriptionPrice.toString());

            setEmail(SelectedLiveSubscription.email || "");
            setSecondaryMobile((SelectedLiveSubscription.secondaryMobile && SelectedLiveSubscription.secondaryMobile.trim() !== "") ? SelectedLiveSubscription.secondaryMobile.replace('+', '') : "");
            setCycle(SelectedLiveSubscription.subscriptionType || "");
            setSecondaryPayeeName(SelectedLiveSubscription.secondaryPayeeName || "");
            setSecondaryEmail(SelectedLiveSubscription.secondaryEmail || "");
            if (SelectedLiveSubscription.billingStart) {
                const originalStart = new Date(SelectedLiveSubscription.billingStart);
                setBillingStart(originalStart);
                setPrevBillingStart(originalStart);
            }
            if (SelectedLiveSubscription.billingEnd) {
                const originalEnd = new Date(SelectedLiveSubscription.billingEnd);
                setBillingEnd(originalEnd);
                setPrevBillingEnd(originalEnd);
            }
            setRemainingCount((SelectedLiveSubscription.remainingCount || 0).toString());
            setCyclePriceArray(SelectedLiveSubscription.assignedPlans || []);
            setSelectedCyclePlanIndex(SelectedLiveSubscription.assignedPlanIndex || 0);
            setPrevCyclePlanIndex(SelectedLiveSubscription.assignedPlanIndex || 0);
            setSelectedCyclePlan(SelectedLiveSubscription.assignedPlans ? SelectedLiveSubscription.assignedPlans[SelectedLiveSubscription.assignedPlanIndex || 0] : "");
            setPrevPrice(SelectedLiveSubscription.Price || 0);
            setPayeeName(SelectedLiveSubscription.name || "");
            if (SelectedLiveSubscription.subscriptionType === SubscriptionType.MultiMonth || SelectedLiveSubscription.subscriptionType === SubscriptionType.Count) {
                setCustomPrice(SelectedLiveSubscription.assignedPlans ? SelectedLiveSubscription.assignedPlans[SelectedLiveSubscription.assignedPlanIndex || 0].price.toString() : "");
                setPrevPrice(SelectedLiveSubscription.assignedPlans ? SelectedLiveSubscription.assignedPlans[SelectedLiveSubscription.assignedPlanIndex || 0].price : 0);
            }
            setExcludeLiveSubFromGST(SelectedLiveSubscription.excludeLiveSubFromGST || false)

            // Updated check for products
            if (SelectedLiveSubscription.products &&
                SelectedLiveSubscription.products.length > 0 &&
                Object.keys(SelectedLiveSubscription.products[0]).length > 0) {
                const updatedProducts = SelectedLiveSubscription.products.map(product => ({
                    name: product.name,
                    price: product.price.toString() // Ensure price is stored as string
                }));
                setProducts(updatedProducts);
                setProductErrors(updatedProducts.map(() => ({ name: false, price: false })));
            } else {
                setProducts([{ name: '', price: '' }]);
                setProductErrors([{ name: false, price: false }]);
            }

            // Initialize consumer GST
            setConsumerGst((SelectedLiveSubscription as any).consumerGSTIN || "");
            setChangeToActive(false); // Reset when modal opens or subscription changes
            
            // Initialize KYC responses from existing data
            if (SelectedLiveSubscription.customerKYCResponses && Object.keys(SelectedLiveSubscription.customerKYCResponses).length > 0) {
                setKycResponses(SelectedLiveSubscription.customerKYCResponses);
            }

            const live = SelectedLiveSubscription;
            let cancelled = false;
            const loadCatalogPlans = async () => {
                const st = live.subscriptionType;
                if (
                    (st !== SubscriptionType.MultiMonth && st !== SubscriptionType.Count) ||
                    !live.subscriptionPlanRefId ||
                    !ServiceProviderId ||
                    !open
                ) {
                    return;
                }
                try {
                    const subscriptionDocRef = doc(
                        db,
                        `ServiceProviders/${ServiceProviderId}/Subscriptions/${live.subscriptionPlanRefId}`
                    );
                    const subscriptionSnap = await getDoc(subscriptionDocRef);
                    if (!subscriptionSnap.exists() || cancelled) return;

                    const subData = subscriptionSnap.data();
                    const rawArray = subData.cyclePriceArray;
                    if (!Array.isArray(rawArray) || rawArray.length === 0) {
                        if (!cancelled) setSubscriptionDocPlans([]);
                        return;
                    }

                    const catalogNormalized = rawArray.map((r: any) => normalizeSubscriptionCycleEntry(r, st));
                    if (cancelled) return;
                    setSubscriptionDocPlans(catalogNormalized);
                } catch (err) {
                    console.error("Error loading subscription plan catalog:", err);
                }
            };

            void loadCatalogPlans();

            return () => {
                cancelled = true;
            };
        }
    }, [SelectedLiveSubscription, open, ServiceProviderId])

    // Fetch CustomerKYC fields when modal opens
    useEffect(() => {
        const fetchCustomerKYCFields = async () => {
            if (ServiceProviderId && open) {
                try {
                    const serviceProviderRef = doc(db, 'ServiceProviders', ServiceProviderId);
                    const serviceProviderDoc = await getDoc(serviceProviderRef);
                    
                    if (serviceProviderDoc.exists()) {
                        const data = serviceProviderDoc.data();
                        const kycFields = data.customerKYCFields || [];
                        setCustomerKYCFields(kycFields);
                        
                        // Initialize KYC responses if not already set from existing data
                        
                    }
                } catch (error) {
                    console.error("Error fetching CustomerKYC fields:", error);
                }
            }
        };

        fetchCustomerKYCFields();
    }, [ServiceProviderId, open, kycResponses]);


    // ------------------------Handle Change Form States

    const handleChangeSubscriberName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubscriberName(event.target.value)
    }
    const { uploadFileInFirestore } = useBackend("")

    async function handlePhotoChange(e: any) {
        const file = e.target.files[0];

        const url: string = await uploadFileInFirestore(ServiceProviderId, file)
        setProfilePhoto(url)
    }

    const handleChangeOutsideState = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOutsideState(event.target.checked);
    };

    const handleChangeDiscount = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // -------------Function to handle only decimal and number inputs
        if (!value) {
            setDiscount("")
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        setDiscount(value)
    }


    const handleChangeCustomPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        // -------------Function to handle only decimal and number inputs
        if (!value) {
            setCustomPrice("")
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') { /* empty */
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        setCustomPrice(value)
    }


    const handleChangeMultiplier = (event: { target: { value: any; }; }) => {
        setMultiplier(Math.max(Number(event.target.value), 1));
    };

    const handleChangeMonthlyPrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value) {
            setMonthlyPrice("");
            return;
        }
        let decimalCount = 0;
        for (let i = 0; i < value.length; i++) {
            const char = value[i];
            if (char >= '0' && char <= '9') {
                // Valid digit
            } else if (char === '.') {
                if (decimalCount === 0) {
                    decimalCount++;
                } else {
                    return;
                }
            } else {
                return;
            }
        }
        setMonthlyPrice(value);
    }

    const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const handleChangeSecondaryMobile = (value: string) => {
        setSecondaryMobile(value);
    };

    const handleChangePayeeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPayeeName(event.target.value);
    };

    const handleChangeSecondaryPayeeName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSecondaryPayeeName(event.target.value);
    };

    const handleChangeSecondaryEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSecondaryEmail(event.target.value);
    };

    const handleConsumerGstChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toUpperCase();
        setConsumerGst(value);
    };

    // Handler for KYC field changes
    const handleKYCFieldChange = (fieldId: number, value: any) => {
        setKycResponses(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    const handleChangeRemainingCount = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        // Allow empty string
        if (!value) {
            setRemainingCount("");
            return;
        }

        // Allow negative sign only at the start, digits, and only one decimal point
        const regex = /^-?\d*\.?\d*$/;

        // Additional validation to ensure only one decimal point and negative sign only at start
        const negativeCount = (value.match(/-/g) || []).length;
        const decimalCount = (value.match(/\./g) || []).length;
        const negativeAtStart = value.indexOf('-') === 0 || value.indexOf('-') === -1;

        if (regex.test(value) && negativeCount <= 1 && decimalCount <= 1 && negativeAtStart) {
            setRemainingCount(value);
        }
    };

    const handleCyclePlanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedIndex = Number(event.target.value);
        selectCyclePlanByIndex(selectedIndex);
    };

    const selectCyclePlanByIndex = (selectedIndex: number) => {
        if (selectedIndex < 0 || selectedIndex >= cyclePriceArray.length) return;
        const selected = cyclePriceArray[selectedIndex];
        setSelectedCyclePlanIndex(selectedIndex);
        setSelectedCyclePlan(selected);
        setCustomPrice(selected?.price?.toString() || "");
    };

    /** Index in current `cyclePriceArray` matching a subscription-doc row (assigned + any rows appended this session). */
    const assignedPlanIndexForDocPlan = (docPlan: any): number =>
        assignedPlanIndexInArray(cyclePriceArray, docPlan, cycle);

    /** Pick a catalog plan: select existing assigned row, or append then select the new last index (persisted on submit). */
    const handleSubscriptionCatalogPlanPick = (docPlan: any) => {
        const matchedIdx = assignedPlanIndexInArray(cyclePriceArray, docPlan, cycle);
        if (matchedIdx >= 0) {
            selectCyclePlanByIndex(matchedIdx);
            return;
        }
        const newEntry = livePlanEntryFromDocPlan(docPlan, cycle);
        const next = [...cyclePriceArray, newEntry];
        const newIdx = next.length - 1;
        setCyclePriceArray(next);
        setSelectedCyclePlanIndex(newIdx);
        setSelectedCyclePlan(next[newIdx]);
        setCustomPrice(newEntry.price?.toString() ?? "");
    };

    const planRowLabelSx = {
        "& .MuiFormControlLabel-label": {
            fontSize: "14px",
            width: "100%",
        },
        marginBottom: "8px",
        "&:hover": {
            backgroundColor: "transparent",
        },
    } as const;

    // -------------------------------------Error Handlers For Modal

    const [modalError, setModalError] = React.useState({
        subscriberName: false,
        payeeNumber: false,
        CollectivePackPrice: false,
        monthlyPrice: false,
        email: false,
        secondaryMobile: false,
        secondaryEmail: false,
        secondaryPayeeName: false,
        customPrice: false,
        payeeName: false,
        consumerGst: false
    });

    const isValidEmail = (email: string): boolean => {
        if (email === '') return true; // Empty email is allowed
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    function CheckErrors(): boolean {
        setIsError(false);
        let localerror: boolean = false;


        if (payeeName.trim() === "") {
            setIsError(true);
            localerror = true;
            setModalError(prev => ({
                ...prev,
                payeeName: true
            }));
        }
        if (subscriberName.trim() === "") {
            setIsError(true);
            localerror = true;
            setModalError(prev => ({
                ...prev,
                subscriberName: true
            }));
        } else {
            setModalError(prev => ({
                ...prev,
                subscriberName: false
            }));
        }

        if (cycle === SubscriptionType.Monthly && SelectedLiveSubscription?.serviceType !== "CableTV" && (monthlyPrice.trim() === "" || Number(monthlyPrice) < 6)) {
            setIsError(true);
            localerror = true;
            setModalError(prev => ({
                ...prev,
                monthlyPrice: true
            }));
        } else {
            setModalError(prev => ({
                ...prev,
                monthlyPrice: false
            }));
        }

        if ((cycle === SubscriptionType.MultiMonth || cycle === SubscriptionType.Count) && (customPrice.trim() === "" || Number(customPrice) < 6)) {
            setIsError(true);

            localerror = true;
            setModalError(prev => ({
                ...prev,
                customPrice: true
            }));
        }

        // Check product errors
        products.forEach((product, index) => {
            if (products.length === 1 && SelectedLiveSubscription?.serviceType !== "CableTV") {
                // If only one product exists and it's not CableTV, don't validate
                return;
            }

            // For CableTV or when more than one product exists
            if (product.name.trim() === '' || product.price.trim() === '') {
                setProductErrors(prev => prev.map((err, i) =>
                    i === index ? { name: product.name.trim() === '', price: product.price.trim() === '' } : err
                ));
                localerror = true;
            }
        });

        // Email validation
        if (email !== '' && !isValidEmail(email)) {
            setIsError(true);
            localerror = true;
            setModalError(prev => ({
                ...prev,
                email: true
            }));
        } else {
            setModalError(prev => ({
                ...prev,
                email: false
            }));
        }

        // Phone validation - PhoneInput validation
        if (secondaryMobile !== '' && secondaryMobile.length < 6) {
            setIsError(true);
            localerror = true;
            setShowAdvanced(true)
            setModalError(prev => ({
                ...prev,
                secondaryMobile: true
            }));
        } else {
            setModalError(prev => ({
                ...prev,
                secondaryMobile: false
            }));
        }

        if (secondaryEmail !== "" && !isValidEmail(secondaryEmail)) {
            setIsError(true);
            localerror = true;
            setShowAdvanced(true)
            setModalError(prev => ({
                ...prev,
                secondaryEmail: true
            }));
        } else {
            setModalError(prev => ({
                ...prev,
                secondaryEmail: false
            }));
        }

        // Consumer GST validation (only if a value is provided)
        if (showAdvanced && consumerGst.trim() !== "") {
            const gstinRegex = /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}Z[0-9A-Za-z]{1}$/;
            if (!gstinRegex.test(consumerGst)) {
                setIsError(true)
                localerror = true
                setConsumerGstError(true)
                setModalError(prev => ({
                    ...prev,
                    consumerGst: true
                }));
            } else {
                setConsumerGstError(false)
                setModalError(prev => ({
                    ...prev,
                    consumerGst: false
                }));
            }
        } else {
            setConsumerGstError(false)
            setModalError(prev => ({
                ...prev,
                consumerGst: false
            }));
        }

        return localerror;
    }

    // --------------------------On Submit Operation

    const { update } = useBackend(`/ServiceProviders/${ServiceProviderId}/LiveSubscriptions`)

    async function onSubmit(e: any) {
        Dispatch(toggleLoading(true))

        e.preventDefault()
        const Localerrors = CheckErrors()

        if (Localerrors) {
            Dispatch(toggleLoading(false))
            return
        }

        const formattedProducts = products.map((product, index) => {
            if (products.length === 1) {
                // Handle single product case
                if (product.name.trim() === '') {
                    return {}; // Send empty object if name is not filled
                } else {
                    return {
                        name: product.name,
                        price: typeof product.price === 'string'
                            ? (product.price.trim() === '' ? 0 : Number(product.price))
                            : product.price
                    };
                }
            } else {
                // Handle multiple products case
                return {
                    name: product.name,
                    price: typeof product.price === 'string'
                        ? (product.price.trim() === '' ? 0 : Number(product.price))
                        : product.price
                };
            }
        });

        const updateData: any = {
            studentName: subscriberName,
            photoUrl: profilePhoto,
            email: email,
            secondaryMobile: secondaryMobile ? "+" + secondaryMobile : "",
            updatedAt: Timestamp.fromDate(new Date()),
            secondaryPayeeName: secondaryPayeeName,
            secondaryEmail: secondaryEmail,
            discountNegotiated: Number(discount),
            outsideState: outsideState,
            name: payeeName,
            consumerGSTIN: consumerGst || "",
            excludeLiveSubFromGST: excludeLiveSubFromGST
        };

        // Add KYC responses if any
        if (Object.keys(kycResponses).length > 0) {
            updateData.customerKYCResponses = kycResponses;
        }



        if (cycle !== SubscriptionType.MultiMonth && cycle !== SubscriptionType.Count) {
            if (SelectedLiveSubscription?.serviceType !== "CableTV") {
                updateData.Price = monthlyPrice.trim() === '' ? 0 : Number(monthlyPrice);
            }
            updateData.priceMultiplier = multiplier;
            updateData.products = formattedProducts;
        } else {
            const updatedAssignedPlans = cyclePriceArray.map((plan, index) =>
                index === selectedCyclePlanIndex ? { ...plan, price: Number(customPrice) } : plan
            );
            updateData.assignedPlans = updatedAssignedPlans;
            updateData.assignedPlanIndex = selectedCyclePlanIndex;
            updateData.billingCycle = updatedAssignedPlans[selectedCyclePlanIndex]?.billingCycle;
        }

        if ((cycle === SubscriptionType.MultiMonth || cycle === SubscriptionType.Count) && SelectedLiveSubscription?.status !== "inactive") {
            updateData.billingStart = Timestamp.fromDate(billingStart);
            updateData.billingEnd = Timestamp.fromDate(billingEnd);
        }

        if (cycle === SubscriptionType.Count) {
            updateData.remainingCount = remainingCount === "" ? 0 : Number(remainingCount);
        }

        // Add status update if checkbox is checked
        if (SelectedLiveSubscription?.status === "InActive" && changeToActive) {
            updateData.status = "Active";
        }

        try {
            await update(LiveSubscriptionId, updateData);
            handleClose();
            toast.success("Student details updated successfully!");

        } catch (error) {
            console.error("Error updating subscription:", error);
            toast.error("Failed to update subscription. Please try again.");
        }
        Dispatch(toggleLoading(false))

        logAction({
            action: ActionType.editLiveSubscriptionPrice,
            timestamp: Timestamp.now(),
            platform: "web",
            performedBy: {
                spId: spId || "",
                name: spName || ""
            },
            livesubscriptionId: LiveSubscriptionId,
            context: {
                livesubscriptionId: LiveSubscriptionId,
                prevPrice: prevPrice,
                newPrice: SelectedLiveSubscription.subscriptionType === SubscriptionType.Monthly ? Number(monthlyPrice) : Number(customPrice),
                newAssignedPlanIndex: SelectedLiveSubscription.subscriptionType === SubscriptionType.Monthly ? "N/A" : selectedCyclePlanIndex,
                prevAssignedPlanIndex: SelectedLiveSubscription.subscriptionType === SubscriptionType.Monthly ? "N/A" : prevCyclePlanIndex,
                previousStartDate: prevBillingStart.toISOString(),
                previousEndDate: prevBillingEnd.toISOString(),
                currentStartDate: billingStart.toISOString(),
                currentEndDate: billingEnd.toISOString(),
            },
        }, ServiceProviderId)
    }

    // const handleAddProduct = () => {
    //     setProducts([...products, { name: '', price: '0' }]);
    //     setProductErrors([...productErrors, { name: false, price: false }]);
    // };

    // const handleRemoveProduct = (index: number) => {
    //     if (products.length > 1) {
    //         const newProducts = products.filter((_, i) => i !== index); 
    //         const newErrors = productErrors.filter((_, i) => i !== index); 
    //         setProducts(newProducts);
    //         setProductErrors(newErrors);
    //     }
    // };

    // const handleProductChange = (index: number, field: 'name' | 'price', value: string) => {
    //     const newProducts = [...products];
    //     const newErrors = [...productErrors];

    //     if (field === 'price') {
    //         const regex = /^\d*\.?\d*$/;
    //         if (regex.test(value) || value === '') {
    //             newProducts[index][field] = value;
    //             newErrors[index] = { ...newErrors[index], [field]: value === '' };
    //         }
    //     } else {
    //         newProducts[index][field] = value;
    //         newErrors[index] = { ...newErrors[index], [field]: value.trim() === '' };
    //     }

    //     setProducts(newProducts);
    //     setProductErrors(newErrors);
    // };

    // Render individual KYC fields
    const renderKYCField = (field: CustomerKYCFieldType) => {
        const currentValue = kycResponses[field.id] || '';
        
        switch (field.type) {
            case 'textfield':
                return (
                    <TextField
                        key={field.id}
                        value={currentValue}
                        onChange={(e) => handleKYCFieldChange(field.id, e.target.value)}
                        size="small"
                        label={field.fieldDescription}
                        variant="outlined"
                        fullWidth
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey',
                                    borderRadius: '10px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                },
                            },
                            fontWeight: 400,
                            width: '100%',
                            maxWidth: '100%',
                            marginBottom: '16px'
                        }}
                    />
                );
            
            case 'dropdown':
                return (
                    <TextField
                        key={field.id}
                        select
                        value={currentValue}
                        onChange={(e) => handleKYCFieldChange(field.id, e.target.value)}
                        size="small"
                        label={field.fieldDescription}
                        variant="outlined"
                        fullWidth
                        SelectProps={{
                            native: true,
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey',
                                    borderRadius: '10px'
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'skyblue',
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: '14px',
                                },
                            },
                            fontWeight: 400,
                            width: '100%',
                            maxWidth: '100%',
                            marginBottom: '16px'
                        }}
                    >
                        {field.options?.map((option, index) => (
                            <option key={index} value={option}>
                                {option}
                            </option>
                        ))}
                    </TextField>
                );
            
            case 'radio':
                return (
                    <div key={field.id} className="mb-4">
                        <FormLabel component="legend" className="text-gray-800 font-medium mb-2">
                            {field.fieldDescription}
                        </FormLabel>
                        <RadioGroup
                            value={currentValue}
                            onChange={(e) => handleKYCFieldChange(field.id, e.target.value)}
                            row
                        >
                            {field.options?.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={option}
                                    control={<Radio size="small" />}
                                    label={option}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '14px',
                                        }
                                    }}
                                />
                            ))}
                        </RadioGroup>
                    </div>
                );
            
            case 'checkbox':
                return (
                    <FormControlLabel
                        key={field.id}
                        control={
                            <Checkbox 
                                checked={currentValue || false} 
                                onChange={(e) => handleKYCFieldChange(field.id, e.target.checked)}
                                size="small"
                            />
                        }
                        label={field.fieldDescription}
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                fontSize: '14px',
                            },
                            marginBottom: '16px',
                            width: '100%',
                            maxWidth: '100%'
                        }}
                    />
                );
            
            default:
                return null;
        }
    };

    return (<React.Fragment>

        <Dialog
            sx={{
                padding: 0,
                '& .MuiPaper-root': {
                    borderRadius: '10px',
                    width: { xs: 'calc(100% - 32px)', sm: 'auto' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '600px', md: '800px' },
                    minWidth: { sm: '600px', md: '800px' },
                    margin: { xs: '16px', sm: 'auto' },
                    maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' },
                    overflow: 'hidden',
                    transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms !important'
                },
            }}
            maxWidth="md"
            fullWidth
            open={open}
            onClose={handleClose}
            PaperProps={{
                component: 'div',
            }}
        >
            <DialogContent sx={{ paddingX: { xs: "16px", sm: "40px", md: "60px" }, paddingY: { xs: "24px", sm: "40px", md: "50px" }, maxHeight: { xs: 'calc(100vh - 32px)', sm: '90vh' }, minHeight: { sm: '70vh' }, overflowY: 'auto' }} className={"bg-white"}>
                <div className={"header pb-[20px]"}>
                    <p className={"text-cf_heading font-[600] "}>Edit Student Details</p>
                    {/*<p className={"text-cf_small font-[400] "}>Add New Subscribers to {subscriptionName}</p>*/}
                </div>

                <div className={"absolute right-4 top-4"}><IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton></div>

                <div className={"pt-0"}>
                    <div className={""}>
                        <div className={""}>

                            <Box
                                component={"div"}
                                sx={{}}
                            >
                                <div className="flex flex-cl justify-cente w-full items-center gap-5">
                                    <img className="inline-block size-24 rounded-full ring-2 ring-white"
                                        src={profilePhoto || "https://firebasestorage.googleapis.com/v0/b/coachprodev.appspot.com/o/Config%2FProfilePhoto2Update.png?alt=media&token=53ea668c-04f3-493a-911d-15eba1ce897c"}
                                        alt="Profile" />
                                    <div>
                                        <div>
                                            <input
                                                type="file"
                                                id="upload-photo"
                                                accept="image/*"
                                                style={{ display: 'none' }}
                                                onChange={handlePhotoChange}
                                            />
                                            <label htmlFor="upload-photo"
                                                className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none cursor-pointer">
                                                <svg className="flex-shrink-0 size-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="20" height="20" viewBox="0 0 24 24" fill="none"
                                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                    strokeLinejoin="round">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="17 8 12 3 7 8" />
                                                    <line x1="12" x2="12" y1="3" y2="15" />
                                                </svg>
                                                Upload photo
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className={"TextFields flex flex-col gap-4 mt-8"}>
                                    <div className={"flex items-center "}>
                                        <TextField
                                            onChange={handleChangeSubscriberName}
                                            value={subscriberName}
                                            size={"small"}
                                            required={true}
                                            id="Subscriber Name"
                                            label="Student Name"
                                            error={modalError.subscriberName}
                                            helperText={modalError.subscriberName ? "Student Name is required" : ""}
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey', borderRadius: '10px'// Default border color
                                                    }, '&:hover fieldset': {
                                                        borderColor: 'grey', // Border color on hover
                                                    }, '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue', // Border color when focused
                                                    }, '& .MuiInputBase-input': {
                                                        fontSize: '14px', // Setting font size
                                                    },
                                                }, fontWeight: 400, width: '100%', maxWidth: '100%'
                                            }}
                                        />

                                    </div>

                                    <div className={"flex items-center "}>
                                        <TextField
                                            onChange={handleChangeEmail}
                                            value={email}
                                            size={"small"}
                                            id="email"
                                            label="Email (Optional)"
                                            type="email"
                                            error={modalError.email}
                                            helperText={modalError.email ? "Please enter a valid email address" : ""}
                                            variant="outlined"
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: '100%',
                                                maxWidth: '100%'
                                            }}
                                        />
                                    </div>





                                    {cycle === SubscriptionType.Count && (
                                        <TextField
                                            value={remainingCount}
                                            size="small"
                                            id="remainingCount"
                                            label="Remaining Classes"
                                            onChange={handleChangeRemainingCount}
                                            variant="outlined"
                                            placeholder="e.g., 10, -2, 5.5"
                                            fullWidth
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: '100%',
                                                maxWidth: '100%'
                                            }}
                                        />
                                    )}

                                    {(cycle === SubscriptionType.MultiMonth || cycle === SubscriptionType.Count) && (
                                        <>
                                            {cycle === SubscriptionType.MultiMonth && (
                                                <>
                                                    {!showChangePlan ? <div>
                                                        <span className="text-gray-800 font-semibold">Selected Plan : </span> <span onClick={() => setShowChangePlan(true)} className="text-cc_brand hover:underline hover:cursor-pointer font-medium  pl-4 text-sm">Change Plan?</span>
                                                        <div className="grid grid-cols-4 gap-4 my-2 pl-10 pr-4">
                                                            <div className="text-gray-500 text-sm">Plan Name</div>
                                                            <div className="text-gray-500 text-sm">Price</div>
                                                            <div className="text-gray-500 text-sm">Duration (days)</div>
                                                            <div></div>
                                                        </div><div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                                            <div className=" text-sm">{selectedCyclePlan?.billingCycle}</div>
                                                            <div className="t text-sm">₹{selectedCyclePlan?.price}</div>
                                                            <div className=" text-sm">{selectedCyclePlan?.validity} days</div>
                                                            <div></div>
                                                        </div>

                                                    </div> : <div className="mb-2">
                                                        <div className="text-gray-800 font-semibold mb-2">
                                                            Select New Plan
                                                        </div>
                                                        <p className="text-red-500 text-sm  mb-2.5">Note: All the changes will be effective from the next billing cycle</p>
                                                        <RadioGroup
                                                            value={selectedCyclePlanIndex.toString()}
                                                            onChange={handleCyclePlanChange}
                                                        >
                                                            <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                                                <div className="text-gray-500 text-sm">Plan Name</div>
                                                                <div className="text-gray-500 text-sm">Price</div>
                                                                <div className="text-gray-500 text-sm">Duration (days)</div>
                                                                <div></div>
                                                            </div>
                                                            {cyclePriceArray.map((plan, index) => (
                                                                <FormControlLabel
                                                                    key={index}
                                                                    value={index.toString()}
                                                                    control={<Radio size="small" />}
                                                                    label={
                                                                        <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm p-2">
                                                                            <div className="pl-2">{plan?.billingCycle}</div>
                                                                            <div>₹{plan?.price}</div>
                                                                            <div>{plan?.validity} days</div>
                                                                        </div>
                                                                    }
                                                                    sx={planRowLabelSx}
                                                                />
                                                            ))}
                                                        </RadioGroup>

                                                        {subscriptionDocPlans.length > 0 && (
                                                            <div className="mt-5 rounded-xl border-2 border-dashed border-sky-400/70 bg-sky-50/60 p-3">
                                                                <div className="mb-3 text-sm font-semibold text-sky-950">
                                                                    Plans from subscription
                                                                </div>
                                                                <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                                                    <div className="text-gray-500 text-sm">Plan Name</div>
                                                                    <div className="text-gray-500 text-sm">Price</div>
                                                                    <div className="text-gray-500 text-sm">Duration (days)</div>
                                                                    <div />
                                                                </div>
                                                                {subscriptionDocPlans.map((plan, idx) => {
                                                                    const matchedIdx = assignedPlanIndexForDocPlan(plan);
                                                                    return (
                                                                        <FormControlLabel
                                                                            key={`subdoc-mm-${idx}`}
                                                                            control={
                                                                                <Radio
                                                                                    size="small"
                                                                                    checked={
                                                                                        matchedIdx >= 0 &&
                                                                                        selectedCyclePlanIndex === matchedIdx
                                                                                    }
                                                                                    onChange={() =>
                                                                                        handleSubscriptionCatalogPlanPick(plan)
                                                                                    }
                                                                                />
                                                                            }
                                                                            onClick={() =>
                                                                                handleSubscriptionCatalogPlanPick(plan)
                                                                            }
                                                                            label={
                                                                                <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm p-2">
                                                                                    <div className="pl-2">
                                                                                        {plan?.billingCycle}
                                                                                    </div>
                                                                                    <div>₹{plan?.price}</div>
                                                                                    <div>{plan?.validity} days</div>
                                                                                </div>
                                                                            }
                                                                            sx={{
                                                                                ...planRowLabelSx,
                                                                                cursor: "pointer",
                                                                                alignItems: "flex-start",
                                                                            }}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>}
                                                </>
                                            )}

                                            {cycle === SubscriptionType.Count && (
                                                <>
                                                    {!showChangePlan ? <div>
                                                        <span className="text-gray-800 font-semibold">Selected Plan : </span> <span onClick={() => setShowChangePlan(true)} className="text-cc_brand hover:underline hover:cursor-pointer font-medium  pl-4 text-sm">Change Plan?</span>
                                                        <div className="grid grid-cols-4 gap-4 my-2 pl-10 pr-4">
                                                            <div className="text-gray-500 text-sm">No of Classes</div>
                                                            <div className="text-gray-500 text-sm">Price</div>
                                                            <div className="text-gray-500 text-sm">Validity (days)</div>
                                                            <div></div>
                                                        </div><div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                                            <div className=" text-sm">{selectedCyclePlan?.count}</div>
                                                            <div className="t text-sm">₹{selectedCyclePlan?.price}</div>
                                                            <div className=" text-sm">{selectedCyclePlan?.validity} days</div>
                                                            <div></div>
                                                        </div>

                                                    </div> : <div className="mb-2">
                                                        <div className="text-gray-800 font-semibold mb-2">
                                                            Select New Plan
                                                        </div>
                                                        <p className="text-red-500 text-xs mb-2.5">Note: All the changes will be effective from the next billing cycle</p>
                                                        <RadioGroup
                                                            value={selectedCyclePlanIndex.toString()}
                                                            onChange={handleCyclePlanChange}
                                                        >
                                                            <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                                                <div className="text-gray-500 text-sm">No of Classes</div>
                                                                <div className="text-gray-500 text-sm">Price</div>
                                                                <div className="text-gray-500 text-sm">Validity (days)</div>
                                                                <div></div>
                                                            </div>
                                                            {cyclePriceArray.map((plan, index) => (
                                                                <FormControlLabel
                                                                    key={index}
                                                                    value={index.toString()}
                                                                    control={<Radio size="small" />}
                                                                    label={
                                                                        <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm p-2">
                                                                            <div className="pl-2">{plan?.count}</div>
                                                                            <div>₹{plan?.price}</div>
                                                                            <div>{plan?.validity} days</div>
                                                                        </div>
                                                                    }
                                                                    sx={planRowLabelSx}
                                                                />
                                                            ))}
                                                        </RadioGroup>

                                                        {subscriptionDocPlans.length > 0 && (
                                                            <div className="mt-5 rounded-xl border-2 border-dashed border-sky-400/70 bg-sky-50/60 p-3">
                                                                <div className="mb-3 text-sm font-semibold text-sky-950">
                                                                    Plans from subscription
                                                                </div>
                                                                <div className="grid grid-cols-4 gap-4 mb-2 pl-10 pr-4">
                                                                    <div className="text-gray-500 text-sm">No of Classes</div>
                                                                    <div className="text-gray-500 text-sm">Price</div>
                                                                    <div className="text-gray-500 text-sm">Validity (days)</div>
                                                                    <div />
                                                                </div>
                                                                {subscriptionDocPlans.map((plan, idx) => {
                                                                    const matchedIdx = assignedPlanIndexForDocPlan(plan);
                                                                    return (
                                                                        <FormControlLabel
                                                                            key={`subdoc-ct-${idx}`}
                                                                            control={
                                                                                <Radio
                                                                                    size="small"
                                                                                    checked={
                                                                                        matchedIdx >= 0 &&
                                                                                        selectedCyclePlanIndex === matchedIdx
                                                                                    }
                                                                                    onChange={() =>
                                                                                        handleSubscriptionCatalogPlanPick(plan)
                                                                                    }
                                                                                />
                                                                            }
                                                                            onClick={() =>
                                                                                handleSubscriptionCatalogPlanPick(plan)
                                                                            }
                                                                            label={
                                                                                <div className="grid grid-cols-4 gap-4 w-full bg-white rounded-lg shadow-sm p-2">
                                                                                    <div className="pl-2">{plan?.count}</div>
                                                                                    <div>₹{plan?.price}</div>
                                                                                    <div>{plan?.validity} days</div>
                                                                                </div>
                                                                            }
                                                                            sx={{
                                                                                ...planRowLabelSx,
                                                                                cursor: "pointer",
                                                                                alignItems: "flex-start",
                                                                            }}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>}
                                                </>
                                            )}

                                            <div className={"flex flex-col gap-4"}>
                                                <div>
                                                    <TextField
                                                        value={customPrice}
                                                        size="small"
                                                        id="customPrice"
                                                        label="Custom Price"
                                                        onChange={handleChangeCustomPrice}
                                                        variant="outlined"
                                                        error={modalError.customPrice}
                                                        helperText={modalError.customPrice ? "Price must be minimum ₹6" : ""}
                                                        fullWidth
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey', borderRadius: '10px'// Default border color
                                                                }, '&:hover fieldset': {
                                                                    borderColor: 'grey', // Border color on hover
                                                                }, '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue', // Border color when focused
                                                                }, '& .MuiInputBase-input': {
                                                                    fontSize: '14px', // Setting font size
                                                                },
                                                            }, fontWeight: 400,

                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {(cycle === SubscriptionType.MultiMonth || cycle === SubscriptionType.Count) && SelectedLiveSubscription?.status !== "inactive" && (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div>
                                                    <p className="text-xs font-medium mb-1 text-gray-600">Billing Start Date</p>
                                                    <DatePicker
                                                        value={dayjs(billingStart)}
                                                        onChange={(newValue) => setBillingStart(newValue ? newValue.toDate() : new Date())}
                                                        format="DD/MM/YYYY"
                                                        maxDate={dayjs(billingEnd)}
                                                        slotProps={{
                                                            textField: {
                                                                size: "small",
                                                                sx: {
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            borderColor: 'grey',
                                                                            borderRadius: '10px',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: 'skyblue',
                                                                        },
                                                                        '& .MuiInputBase-input': {
                                                                            fontSize: '14px',
                                                                        },
                                                                    },
                                                                    width: '100%'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <p className="text-xs font-medium mb-1 text-gray-600">Billing End Date</p>
                                                    <DatePicker
                                                        value={dayjs(billingEnd)}
                                                        onChange={(newValue) => setBillingEnd(newValue ? newValue.toDate() : new Date())}
                                                        format="DD/MM/YYYY"
                                                        minDate={dayjs(billingStart)}
                                                        slotProps={{
                                                            textField: {
                                                                size: "small",
                                                                sx: {
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            borderColor: 'grey',
                                                                            borderRadius: '10px',
                                                                        },
                                                                        '&:hover fieldset': {
                                                                            borderColor: 'grey',
                                                                        },
                                                                        '&.Mui-focused fieldset': {
                                                                            borderColor: 'skyblue',
                                                                        },
                                                                        '& .MuiInputBase-input': {
                                                                            fontSize: '14px',
                                                                        },
                                                                    },
                                                                    width: '100%'
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </LocalizationProvider>
                                    )}

                                    {(cycle !== SubscriptionType.MultiMonth && cycle !== SubscriptionType.Count) && (
                                        <>
                                            {SelectedLiveSubscription?.serviceType !== "CableTV" && (
                                                <TextField
                                                    value={monthlyPrice}
                                                    size="small"
                                                    id="MonthlyPrice"
                                                    label="Monthly Price"
                                                    error={modalError.monthlyPrice}
                                                    helperText={modalError.monthlyPrice ? "Price must be minimum ₹6" : ""}
                                                    required={true}
                                                    onChange={handleChangeMonthlyPrice}
                                                    variant="outlined"
                                                    fullWidth
                                                    InputProps={{
                                                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: 'grey',
                                                                borderRadius: '10px'
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'grey',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'skyblue',
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '14px',
                                                            },
                                                        },
                                                        fontWeight: 400,
                                                        width: '100%',
                                                        maxWidth: '100%'
                                                    }}
                                                />
                                            )}

                                            {<div className={"w-full max-w-full"}>
                                                {SelectedLiveSubscription?.serviceType !== "CableTV" && (
                                                    <div className={"flex gap-4"}>
                                                        <div className={"flex"}>
                                                            <TextField
                                                                value={discount}
                                                                size="small"
                                                                id="Discount"
                                                                label="Discount"
                                                                onChange={handleChangeDiscount}
                                                                variant="outlined"
                                                                fullWidth
                                                                InputProps={{
                                                                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                                }}
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        '& fieldset': {
                                                                            borderColor: 'grey', borderRadius: '10px'// Default border color
                                                                        }, '&:hover fieldset': {
                                                                            borderColor: 'grey', // Border color on hover
                                                                        }, '&.Mui-focused fieldset': {
                                                                            borderColor: 'skyblue', // Border color when focused
                                                                        }, '& .MuiInputBase-input': {
                                                                            fontSize: '14px', // Setting font size
                                                                        },
                                                                    }, fontWeight: 400,

                                                                }}
                                                            />
                                                            <Tooltip
                                                                title="Note: Discount chosen shall be applied for every cycle"
                                                                arrow>
                                                                <button className={"ml-2"} onClick={(e) => e.preventDefault()}>
                                                                    <InfoIcon />
                                                                </button>

                                                            </Tooltip>
                                                        </div>

                                                        {cycle === SubscriptionType.Monthly && showMultiplier && <div className={"flex"}>
                                                            <ButtonGroup>
                                                                <StyledButton

                                                                    onClick={() => setMultiplier((prev) => prev - 1)}
                                                                    disabled={multiplier === 1}
                                                                >
                                                                    <RemoveIcon fontSize="small" />
                                                                </StyledButton>
                                                                <StyledInput sx={{ width: 80 }} size="small"
                                                                    onChange={handleChangeMultiplier}
                                                                    value={multiplier} disabled

                                                                />
                                                                <StyledButton onClick={() => setMultiplier((prev) => prev + 1)}>
                                                                    <AddIcon fontSize="small" />
                                                                </StyledButton>
                                                            </ButtonGroup>
                                                            <Tooltip
                                                                title="Use a multiplier if the subscription price is based on units. For example, if the subscription price is 100 Rs per hour-month, setting the multiplier to 5 will cost the student 500 Rs."
                                                                arrow>
                                                                <button className={"ml-2"} onClick={(e) => e.preventDefault()}>
                                                                    <InfoIcon />
                                                                </button>
                                                            </Tooltip>
                                                        </div>}
                                                    </div>
                                                )}
                                            </div>}


                                            {/* FIXME: pRODUCT cODE IS HIDED AS OF NOW, REENEABLE IT WHEN NEEDED */}
                                            {/* {products.map((product, index) => (
                                                <div key={index} className="flex items-center gap-2 mb-2">
                                                    <TextField
                                                        value={product.name}
                                                        size="small"
                                                        label={`Product ${index + 1} Name`}
                                                        onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                                                        variant="outlined"
                                                        error={productErrors[index]?.name}
                                                        helperText={productErrors[index]?.name ? "Required" : ""}
                                                        placeholder="Eg. Food"

                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey', borderRadius: '10px'// Default border color
                                                                }, '&:hover fieldset': {
                                                                    borderColor: 'grey', // Border color on hover
                                                                }, '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue', // Border color when focused
                                                                }, '& .MuiInputBase-input': {
                                                                    fontSize: '14px', // Setting font size
                                                                },
                                                            }, fontWeight: 400, width: '250px'
                                                        }}
                                                    />
                                                    <TextField
                                                        value={product.price}
                                                        size="small"
                                                        label={`Product ${index + 1} Price`}
                                                        onChange={(e) => handleProductChange(index, 'price', e.target.value)}
                                                        variant="outlined"
                                                        error={products.length > 1 && productErrors[index]?.price}
                                                        helperText={products.length > 1 && productErrors[index]?.price ? "Required" : ""}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                                        }}

                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                '& fieldset': {
                                                                    borderColor: 'grey', borderRadius: '10px'// Default border color
                                                                }, '&:hover fieldset': {
                                                                    borderColor: 'grey', // Border color on hover
                                                                }, '&.Mui-focused fieldset': {
                                                                    borderColor: 'skyblue', // Border color when focused
                                                                }, '& .MuiInputBase-input': {
                                                                    fontSize: '14px', // Setting font size
                                                                },
                                                            }, fontWeight: 400, width: '150px'
                                                        }}
                                                    />
                                                    {index === products.length - 1 && (
                                                        <IconButton onClick={handleAddProduct} size="small">
                                                            <AddIcon />
                                                        </IconButton>
                                                    )}
                                                    {products.length > 1 && (
                                                        <IconButton onClick={() => handleRemoveProduct(index)} size="small">
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </div>
                                            ))} */}
                                        </>
                                    )}

                                    {/* Change Student to Active Checkbox - only if status is InActive */}
                                    {SelectedLiveSubscription?.status === "InActive" && (
                                        <div className="flex items-center gap-2 mb-2">
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={!!changeToActive}
                                                        onChange={e => setChangeToActive(e.target.checked)}
                                                    />
                                                }
                                                label="Change Student to Active"
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '14px',
                                                    }
                                                }}
                                            />
                                            <Tooltip title="Please note: This change is irreversible." arrow>
                                                <button className="ml-1" onClick={e => e.preventDefault()}>
                                                    <InfoIcon />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    )}

                                    <div className="flex items-center gap-2">
                                        <FormControlLabel control={<Checkbox checked={showAdvanced}
                                            onChange={(e) => setShowAdvanced(e.target.checked)} />}
                                            label="Show advanced" sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '14px', // Setting the font size for the label
                                                }
                                            }} />
                                    </div>

                                    {showAdvanced && (

                                        <div className="flex flex-col gap-4">

                                            <TextField
                                                value={payeeName}
                                                size="small"
                                                label="Payee Name"
                                                onChange={handleChangePayeeName}
                                                variant="outlined"
                                                error={modalError.payeeName}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: 'grey',
                                                            borderRadius: '10px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'grey',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'skyblue',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                    width: '100%',
                                                    maxWidth: '100%'
                                                }}
                                            />


                                            <TextField
                                                value={secondaryPayeeName}
                                                size="small"
                                                label="Secondary Payee Name (Optional)"
                                                onChange={handleChangeSecondaryPayeeName}
                                                variant="outlined"
                                                fullWidth
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: 'grey',
                                                            borderRadius: '10px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'grey',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'skyblue',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                    width: '100%',
                                                    maxWidth: '100%'
                                                }}
                                            />

                                            <div className="">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Mobile (Optional)</label>
                                                <PhoneInput
                                                    country={'in'}
                                                    value={secondaryMobile}
                                                    onChange={handleChangeSecondaryMobile}
                                                    inputStyle={{
                                                        width: '100%',
                                                        height: '40px',
                                                        fontSize: '14px',
                                                        borderRadius: '10px',
                                                        borderColor: modalError.secondaryMobile ? 'red' : 'grey'
                                                    }}
                                                    containerStyle={{
                                                        width: '100%',
                                                        maxWidth: '100%',
                                                        marginBottom: `0px`
                                                    }}
                                                />
                                                {modalError.secondaryMobile && (
                                                    <p className="text-red-500 text-xs mt-1">Please enter a valid mobile number</p>
                                                )}
                                            </div>

                                            <TextField
                                                value={secondaryEmail}
                                                size="small"
                                                label="Secondary Email (Optional)"
                                                type="email"
                                                onChange={handleChangeSecondaryEmail}
                                                error={modalError.secondaryEmail}
                                                helperText={modalError.secondaryEmail ? "Please enter a valid email address" : ""}
                                                variant="outlined"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        '& fieldset': {
                                                            borderColor: 'grey',
                                                            borderRadius: '10px'
                                                        },
                                                        '&:hover fieldset': {
                                                            borderColor: 'grey',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: 'skyblue',
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            fontSize: '14px',
                                                        },
                                                    },
                                                    fontWeight: 400,
                                                    width: '100%',
                                                    maxWidth: '100%'
                                                }}
                                            />
                                        </div>
                                    )}

                                    {(showAdvanced && !NoGSTBatch) && (
                                        <>  <TextField
                                            onChange={handleConsumerGstChange}
                                            value={consumerGst}
                                            size={"small"}
                                            id="ConsumerGST"
                                            label="Consumer GST (Optional)"
                                            fullWidth={true}
                                            error={consumerGstError}
                                            helperText={consumerGstError ? "Invalid GST format" : ""}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: 'grey',
                                                        borderRadius: '10px'
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: 'grey',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'skyblue',
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        fontSize: '14px',
                                                    },
                                                },
                                                fontWeight: 400,
                                                width: '100%',
                                                maxWidth: '100%',
                                                marginBottom: 2
                                            }}
                                        />

                                        <FormControlLabel
                                            control={<Checkbox checked={excludeLiveSubFromGST} onChange={handleChangeExcludeLiveSubFromGST} />}
                                            label="Exclude Student from GST"
                                            sx={{
                                                '& .MuiFormControlLabel-label': {
                                                    fontSize: '14px',
                                                }
                                            }}
                                        />

                                        </>

                                    )}

                                    {showAdvanced && (
                                        <div className="flex items-center gap-0">
                                            <FormControlLabel
                                                control={<Checkbox checked={outsideState} onChange={handleChangeOutsideState} />}
                                                label="Outside State"
                                                sx={{
                                                    '& .MuiFormControlLabel-label': {
                                                        fontSize: '14px',
                                                    }
                                                }}
                                            />
                                            <Tooltip
                                                className="bg-white"
                                                title="This student will be charged IGST instead of CGST+SGST. Note: This option is only applicable when GST is enabled for your business."
                                                arrow
                                            >
                                                <button onClick={(e) => e.preventDefault()}>
                                                    <InfoIcon />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    )}

                                    {/* KYC Fields */}
                                    {customerKYCFields.length > 0 && (
                                        <div className="w-full max-w-full">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Information</h3>
                                            {customerKYCFields.map((field) => renderKYCField(field))}
                                        </div>
                                    )}

                                </div>
                            </Box>
                        </div>
                    </div>


                </div>

                <div className={"flex gap-2 pt-6"}>
                    <button onClick={(e) => {
                        e.preventDefault();
                        handleClose()
                    }} disabled={isLoading}
                        className={`w-full bg-cc_bg-tertiary border border-solid border-gray-500 p-1.5 rounded-lg ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>Cancel
                    </button>
                    <button
                        onClick={onSubmit} disabled={isLoading}
                        className={`w-full bg-cc_brand text-white p-1.5 rounded-lg border-gray-500 flex justify-center items-center ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}>
                        {isLoading &&
                            <CircularProgress sx={{ color: "white" }} size={20} />}
                        <p className={"ml-2"}>{isLoading ? "Loading" : "Save"}</p>

                    </button>

                </div>
            </DialogContent>

        </Dialog>

    </React.Fragment>);
}